import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { getFamilyTreeIdFromStorage } from "../../../Utils/headOfLineRole";
import {
  getDashboardMemberNameParts,
  isDashboardDeceasedMember,
  isDashboardInLawMember,
  shouldShowDashboardLastName,
} from "../../../Utils/dashboardMemberDisplayName";
import { isExplicitDashboardStartOrTopOfLineMember } from "../../../Utils/dashboardStartOfLineMember";
import {
  getMultipleBirthLegendTags,
  getMultipleBirthSuperscriptText,
} from "../../../Utils/dashboardMultipleBirthSuperscript";
import {
  DashboardDeceasedAngel,
  DashboardInLawAsterisk,
  DashboardPhotoHeadLine,
  DashboardPhotoModal,
  DashboardPhotoName,
  DashboardMemberNameLabel,
  DashboardNameMarkerGroup,
  getDashboardMemberNamePhotoUrls,
  getDashboardMemberPhotoUrl,
  useDashboardPhotoModal,
} from "./DashboardPhotoViewer";
import DashboardBackButton from "./DashboardBackButton";
import DashboardDetailSearchBox, {
  DashboardDetailSearchNoMatch,
} from "./DashboardDetailSearchBox";
import DashboardDetailSortFilterBar from "./DashboardDetailSortFilterBar";
import { DASHBOARD_MEMBER_FILTERS } from "../../../Utils/dashboardDetailMemberFilter";
import DashboardPhotoTapHint from "./DashboardPhotoTapHint";
import DashboardMarkerLegend from "./DashboardMarkerLegend";
import DashboardColorLegend from "./DashboardColorLegend";
import DashboardPeriodDropdown from "./DashboardPeriodDropdown";
import {
  buildDashboardColorLegendLines,
  buildHeadsOfLineIdByColorMap,
  extractAllMembersFromStatisticsResponse,
  extractHeadsOfLineFromStatisticsResponse,
  resolveDashboardFamilyLineHead,
  resolveDashboardFamilyLineHeadName,
} from "../../../Utils/dashboardFamilyLineNavigation";
import { getApi } from "../../../Services/apiService";
import { isLightLineColor } from "../../../Utils/colors";
import { fetchBirthdaysByFamilyLines } from "../../../Utils/dashboardBirthdaysByFamilyLines";
import {
  dashboardSearchMatches,
  filterDashboardMembersBySearch,
} from "../../../Utils/dashboardDetailSearch";

/**
 * Birthdays by Family Lines — the printed rpt_ByLines report in the Family
 * Heads format: one colour-line heading per head of line with the line's
 * count, then every member of that line with their birth month and day, in
 * birthday order. The deceased stay on the list, marked with the angel.
 *
 * Split into two columns the way Family Heads is: the first fills the card's
 * height, the rest runs on past a divider in a second, scrolling column.
 */

/**
 * Where the first column may end. A heading never ends it, and a line never
 * leaves a single name behind at the bottom — heading and name move over to
 * the next column with the rest of the line.
 */
const settleColumnSplit = (items, splitAt) => {
  let at = splitAt;
  while (at > 1 && at < items.length && items[at - 1]?.type === "head") {
    at -= 1;
  }
  if (
    at > 2 &&
    at < items.length &&
    items[at - 2]?.type === "head" &&
    items[at]?.type === "member"
  ) {
    at -= 2;
  }
  return at;
};

const recordId = (record) => {
  const id = record?.id ?? record?.member_id;
  return id == null || id === "" ? "" : String(id);
};

const getMemberSearchName = (member) =>
  [
    member?.first_name,
    member?.last_name,
    member?.suffix,
    member?.special_name,
    member?.display_name,
  ]
    .filter(Boolean)
    .join(" ");

/**
 * The name the row prints, in the short form the family reads everywhere —
 * first name, then suffix or special initial ("Frank, Sr.", "Angres "AD", Sr.").
 * In-laws also carry their last name ("Madison Smith"), as on every other
 * dashboard page; the asterisk still marks who married in.
 */
const getMemberBaseName = (member) => {
  if (member?.first_name) {
    const { baseName } = getDashboardMemberNameParts(member, { appendDescendantSpouse: false });
    if (baseName) return baseName;
  }
  const displayName = String(member?.display_name || "").trim();
  // In-laws and unnamed members keep the last name the display name carries.
  if (shouldShowDashboardLastName(member, displayName)) return displayName;
  return displayName.split(/\s+/)[0] || "";
};

// Compared as printed: stray double spaces in the stored name don't count, and
// numbers read in order, so "UnNamed Boy 2" comes before "UnNamed Boy 3" (and 10).
const getNameSortKey = (member) => getMemberBaseName(member).replace(/\s+/g, " ").trim();

const byName = (a, b) =>
  getNameSortKey(a).localeCompare(getNameSortKey(b), undefined, {
    sensitivity: "base",
    numeric: true,
  });

/** Unknown months/days go last whichever way the list is read. */
const compareNullable = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return a - b;
};

const byBirthday = (a, b) =>
  compareNullable(a.month, b.month) || compareNullable(a.day, b.day) || byName(a, b);

/** Birth date as a timestamp (the util leaves dob as epoch ms), or null. */
const getBirthTime = (member) => {
  const dob = member?.dob ?? member?.date_of_birth;
  if (typeof dob === "number") return Number.isFinite(dob) ? dob : null;
  const time = Date.parse(dob || "");
  return Number.isFinite(time) ? time : null;
};

const byBirthDate = (descending) => (a, b) => {
  const timeA = getBirthTime(a);
  const timeB = getBirthTime(b);
  if (timeA == null && timeB == null) return 0;
  if (timeA == null) return 1;
  if (timeB == null) return -1;
  return descending ? timeB - timeA : timeA - timeB;
};

/** Date is this page's own sort, the way it is on the other birthday pages. */
const SORT_DATE_ASC = "date-asc";
const SORT_DATE_DESC = "date-desc";
// The report already lists each line in birthday order, so that is the default.
const DEFAULT_SORT = SORT_DATE_ASC;
const ALL_LINES = "all";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** The birth month by name — the report's own month_name, else off the month number. */
const getMonthName = (member) =>
  String(member?.month_name || "").trim() || MONTH_NAMES[(member?.month ?? 0) - 1] || "";

const START_TOP_OF_LINE_HEADING = "Start/Top of Line";

const colorKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+line$/, "");

/** Where a line sits in the legend, matched on colour name, else hex; -1 if absent. */
const findLegendIndex = (legendLines, line) => {
  const byColor = line.colorName
    ? legendLines.findIndex((entry) => colorKey(entry.colorName) === colorKey(line.colorName))
    : -1;
  if (byColor !== -1 || !line.hexCode) return byColor;
  return legendLines.findIndex(
    (entry) => String(entry.hexCode || "").toLowerCase() === line.hexCode.toLowerCase()
  );
};

const getLineKey = (line) => String(line.colorName || line.hexCode || line.headName);

/** What a whole line can be found by: the head's name and the line colour. */
const getLineSearchText = (line) =>
  [
    line.displayHeadName,
    line.headName,
    line.colorName,
    line.colorName ? `${line.colorName} Line` : "",
  ]
    .filter(Boolean)
    .join(" ");

/** Offset to the next row, so row gaps and heading padding count too. */
const getMeasuredItemHeight = (wrappers, index) => {
  const wrapper = wrappers[index];
  if (index + 1 < wrappers.length) {
    const delta = wrappers[index + 1].offsetTop - wrapper.offsetTop;
    if (delta > 0) return delta;
  }
  return wrapper.getBoundingClientRect().height;
};

const SortDirectionIcon = ({ direction }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ marginLeft: "4px" }}
    aria-hidden="true"
  >
    <path d={direction === "asc" ? "M18 15l-6-6-6 6" : "M6 9l6 6 6-6"} />
  </svg>
);

function BirthdaysByFamilyLines() {
  const { photos: photoModalPhotos, openPhoto, closePhoto } = useDashboardPhotoModal();
  const [headsMap, setHeadsMap] = useState(() => buildHeadsOfLineIdByColorMap([]));
  const [rosterById, setRosterById] = useState(() => new Map());
  const [lines, setLines] = useState([]);
  const [reportTotal, setReportTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState(DEFAULT_SORT);
  const [lineFilter, setLineFilter] = useState(ALL_LINES);
  const [searchQuery, setSearchQuery] = useState("");
  const [isCompactLayout, setIsCompactLayout] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 768
  );
  const [columnSplitIndex, setColumnSplitIndex] = useState(0);
  const [nameColumnWidths, setNameColumnWidths] = useState({ first: null, second: null });
  const columnsRef = useRef(null);
  const firstColumnRef = useRef(null);
  const measureListRef = useRef(null);
  const pageRef = useRef(null);
  const sortBarRef = useRef(null);
  const [searchWidth, setSearchWidth] = useState(null);
  const [blockWidth, setBlockWidth] = useState(null);

  useEffect(() => {
    const updateLayoutMode = () => setIsCompactLayout(window.innerWidth <= 768);
    updateLayoutMode();
    window.addEventListener("resize", updateLayoutMode);
    return () => window.removeEventListener("resize", updateLayoutMode);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchReport = async () => {
      try {
        setLoading(true);
        const family_tree_id = getFamilyTreeIdFromStorage();
        if (!family_tree_id) {
          setLines([]);
          setReportTotal(0);
          return;
        }

        const report = await fetchBirthdaysByFamilyLines(family_tree_id);
        if (cancelled) return;
        setLines(report.lines);
        setReportTotal(report.totalCount);
      } catch (error) {
        console.error("Error fetching birthdays by family lines:", error);
        if (!cancelled) {
          setLines([]);
          setReportTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchReport();
    return () => {
      cancelled = true;
    };
  }, []);

  // The roster supplies the full member records the Family Members name format
  // reads (suffix, special name, multiple births), photos, and the head-of-line
  // map the heading links through — the report itself carries no head id.
  useEffect(() => {
    const fetchRoster = async () => {
      try {
        const family_tree_id = getFamilyTreeIdFromStorage();
        if (!family_tree_id) return;

        const response = await getApi(
          `/dashboard-statistics-with-details?family_tree_id=${family_tree_id}`
        );
        if (response?.statusCode === 200 && response?.data) {
          const members = extractAllMembersFromStatisticsResponse(response.data);
          setHeadsMap(
            buildHeadsOfLineIdByColorMap(
              extractHeadsOfLineFromStatisticsResponse(response.data),
              members
            )
          );
          setRosterById(
            new Map(
              members
                .map((member) => [recordId(member), member])
                .filter(([id]) => id)
            )
          );
        }
      } catch (error) {
        console.error("Error fetching family lines:", error);
      }
    };

    fetchRoster();
  }, []);

  // The phone footer names the family lines the way Family Members does: off
  // the whole roster and the heads block, so both keys list the same lines.
  const legendLines = useMemo(
    () => buildDashboardColorLegendLines(Array.from(rosterById.values()), headsMap),
    [rosterById, headsMap]
  );

  // Each line's head, found through its colour: the short head name the other
  // pages print, and the head's own record for the photo.
  //
  // Lines read oldest to youngest by who heads them, as the printed report and
  // the Family Lines legend do: the Start/Top of Line couple (generation 1)
  // first, then the Generation 2 heads by birth. The report itself comes back
  // in colour order, so the legend's order is borrowed; a line it does not
  // know keeps the report's order at the end.
  const resolvedLines = useMemo(
    () =>
      lines.map((line) => {
        const legendIndex = findLegendIndex(legendLines, line);
        const lineRef = { color_name: line.colorName, hex_code: line.hexCode };
        const head = resolveDashboardFamilyLineHead(lineRef, headsMap);
        const headId = recordId(head);
        return {
          ...line,
          headId: headId || null,
          headRecord: (headId && rosterById.get(headId)) || head || null,
          legendOrder: legendIndex === -1 ? legendLines.length + line.order : legendIndex,
          // The Start/Top of Line couple heads no Generation 2 line, so neither
          // the heads block nor the report names them — the legend's entry
          // for the colour does ("Joseph, Sr. & Frances"), so the heading and
          // the phone key read the same.
          displayHeadName:
            resolveDashboardFamilyLineHeadName(lineRef, headsMap) ||
            line.headName ||
            legendLines[legendIndex]?.headName ||
            START_TOP_OF_LINE_HEADING,
        };
      })
      .sort((a, b) => a.legendOrder - b.legendOrder),
    [lines, headsMap, rosterById, legendLines]
  );

  const isSearching = searchQuery.trim().length > 0;
  const isFiltered = isSearching || lineFilter !== ALL_LINES;

  const lineOptions = useMemo(
    () => [
      { value: ALL_LINES, label: "All Lines" },
      ...resolvedLines.map((line) => ({
        value: getLineKey(line),
        label: line.colorName
          ? `${line.displayHeadName} • ${line.colorName} Line`
          : line.displayHeadName,
        color: line.hexCode || undefined,
      })),
    ],
    [resolvedLines]
  );

  const visibleLines = useMemo(() => {
    const result = resolvedLines
      .filter((line) => lineFilter === ALL_LINES || getLineKey(line) === lineFilter)
      .map((line) => {
        // Typing the head's name or the line colour brings up the whole line.
        const lineMatches = isSearching && dashboardSearchMatches(getLineSearchText(line), searchQuery);
        let members = line.members.map((member) => {
          const roster = rosterById.get(recordId(member));
          // The report's month/day stay authoritative over the roster's fields,
          // and so does its deceased flag — the roster may not carry one.
          return roster
            ? {
                ...member,
                ...roster,
                month: member.month,
                day: member.day,
                dob: member.dob || roster.dob,
                present_or_deceased: member.present_or_deceased ?? roster.present_or_deceased,
              }
            : member;
        });
        if (!lineMatches) {
          members = filterDashboardMembersBySearch(members, searchQuery, getMemberSearchName);
        }
        if (sortBy === SORT_DATE_ASC) members = [...members].sort(byBirthday);
        if (sortBy === SORT_DATE_DESC) members = [...members].sort((a, b) => byBirthday(b, a));
        // Line Color reads each line's members A–Z, as A–Z does.
        if (sortBy === "alpha-asc" || sortBy === "color") members = [...members].sort(byName);
        if (sortBy === "alpha-desc") members = [...members].sort((a, b) => byName(b, a));
        if (sortBy === "oldest") members = [...members].sort(byBirthDate(false));
        if (sortBy === "youngest") members = [...members].sort(byBirthDate(true));

        return {
          ...line,
          members,
          // A search recounts what is on screen; otherwise the report's own count.
          displayCount: isSearching ? members.length : line.totalCount,
        };
      })
      .filter((line) => line.members.length > 0);

    if (sortBy === "color") {
      return result.sort((a, b) =>
        a.colorName.toLowerCase().localeCompare(b.colorName.toLowerCase())
      );
    }
    return result;
  }, [resolvedLines, rosterById, searchQuery, sortBy, lineFilter, isSearching]);

  // One flat run of rows — line heading, member — so the list can break
  // between any two of them when it splits into columns.
  const items = useMemo(() => {
    const list = [];
    visibleLines.forEach((line) => {
      const lineKey = getLineKey(line);
      list.push({ type: "head", key: `head-${lineKey}`, line });
      line.members.forEach((member, index) => {
        list.push({
          type: "member",
          key: `member-${lineKey}-${recordId(member) || index}`,
          member,
        });
      });
    });
    return list;
  }, [visibleLines]);

  const totalCount = isFiltered
    ? visibleLines.reduce((sum, line) => sum + line.displayCount, 0)
    : reportTotal;

  // The search box ends where the last sort button does, so the two read as one
  // block. Capped by the line it sits on, so a phone keeps it on screen.
  useLayoutEffect(() => {
    const bar = sortBarRef.current?.querySelector(".dashboard-detail-sort-toggle");
    const input = pageRef.current?.querySelector(".dashboard-detail-search-input");
    if (!bar || !input) return undefined;

    const measure = () => {
      const lastButton = bar.lastElementChild;
      if (!lastButton) return;
      const width = Math.round(
        lastButton.getBoundingClientRect().right - input.getBoundingClientRect().left
      );
      setSearchWidth(width > 0 ? width : null);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(bar);
    Array.from(bar.children).forEach((child) => ro.observe(child));
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [loading, lines.length, lineFilter, sortBy]);

  // Month and day line up down each column: a column's names are as wide as
  // the widest name in that column (measured off the hidden copy of the list),
  // so the dates sit three spaces past that column's longest line.
  useLayoutEffect(() => {
    if (loading || items.length === 0) return;
    const measureEl = measureListRef.current;
    if (!measureEl) return;
    const wrappers = measureEl.querySelectorAll(":scope > div");
    const splitAt = columnSplitIndex > 0 ? columnSplitIndex : items.length;
    let first = 0;
    let second = 0;
    wrappers.forEach((wrapper, i) => {
      const name = wrapper.querySelector(".birthdays-by-lines-name-col");
      if (!name) return;
      if (i < splitAt) first = Math.max(first, name.scrollWidth);
      else second = Math.max(second, name.scrollWidth);
    });
    const next = {
      first: first > 0 ? Math.ceil(first) : null,
      second: second > 0 ? Math.ceil(second) : null,
    };
    setNameColumnWidths((prev) =>
      prev.first === next.first && prev.second === next.second ? prev : next
    );
  }, [items, loading, columnSplitIndex]);

  // Split into two columns: the first fills the card's height, the second scrolls.
  useLayoutEffect(() => {
    if (loading || items.length === 0) {
      setColumnSplitIndex(0);
      return;
    }

    // Never split on a phone — one scrolling column instead.
    if (isCompactLayout) {
      setColumnSplitIndex(items.length);
      return;
    }

    const columnsEl = columnsRef.current;
    const measureEl = measureListRef.current;
    if (!columnsEl || !measureEl) return;

    const calculateSplit = () => {
      const availableHeight = columnsEl.clientHeight;
      const wrappers = measureEl.querySelectorAll(":scope > div");
      if (availableHeight <= 0 || wrappers.length === 0) {
        setColumnSplitIndex(items.length);
        return;
      }

      let cumulative = 0;
      let splitAt = wrappers.length;
      for (let i = 0; i < wrappers.length; i++) {
        const height = getMeasuredItemHeight(wrappers, i);
        if (cumulative + height > availableHeight) {
          splitAt = Math.max(1, i);
          break;
        }
        cumulative += height;
      }

      setColumnSplitIndex(settleColumnSplit(items, splitAt));
    };

    calculateSplit();

    const ro = new ResizeObserver(calculateSplit);
    ro.observe(columnsEl);
    window.addEventListener("resize", calculateSplit);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", calculateSplit);
    };
  }, [items, loading, isCompactLayout]);

  const firstColumnItems = columnSplitIndex > 0 ? items.slice(0, columnSplitIndex) : items;
  const secondColumnItems =
    columnSplitIndex > 0 && columnSplitIndex < items.length ? items.slice(columnSplitIndex) : [];
  const isSingleColumnList = secondColumnItems.length === 0;

  // After render, trim the first column if its last row still runs past the bottom.
  // Desktop only: a phone's single column is meant to run past the bottom and
  // scroll, and trimming it there steps down a row per render until React gives
  // up (update depth exceeded) and the page goes blank.
  useLayoutEffect(() => {
    if (isCompactLayout) return;
    if (loading || columnSplitIndex <= 1 || columnSplitIndex >= items.length) return;

    const columnsEl = columnsRef.current;
    const lastRow = firstColumnRef.current?.lastElementChild;
    if (!columnsEl || !lastRow) return;

    if (lastRow.getBoundingClientRect().bottom > columnsEl.getBoundingClientRect().bottom + 0.5) {
      setColumnSplitIndex((prev) => settleColumnSplit(items, Math.max(1, prev - 1)));
    }
  }, [columnSplitIndex, items, loading, isCompactLayout]);

  // On a desktop the search box runs out to where the list's longest line
  // ends, and the sort buttons centre under it, so header and list read as one
  // block. The widest row comes off the hidden copy of the list. Picking one
  // line or searching keeps the full report's width, so the header holds still.
  useLayoutEffect(() => {
    if (isFiltered && blockWidth != null && !isCompactLayout) return undefined;
    if (loading || isCompactLayout || items.length === 0) {
      setBlockWidth(null);
      return undefined;
    }

    const measure = () => {
      const input = pageRef.current?.querySelector(".dashboard-detail-search-input");
      const lists = columnsRef.current?.querySelectorAll(".family-heads-list");
      const measureEl = measureListRef.current;
      const buttons = sortBarRef.current?.querySelector(".dashboard-detail-sort-toggle");
      if (!input || !lists?.length || !measureEl) return;

      // Each column is only as wide as its own longest line, so the list ends
      // where the last column's widest date or heading ends.
      const lastList = lists[lists.length - 1];
      let listRight = lastList.getBoundingClientRect().left + lastList.clientLeft;
      lastList
        .querySelectorAll(".birthdays-by-lines-date-col, .family-heads-head-row > *")
        .forEach((el) => {
          listRight = Math.max(listRight, el.getBoundingClientRect().right);
        });
      const buttonsWidth =
        buttons?.firstElementChild && buttons.lastElementChild
          ? buttons.lastElementChild.getBoundingClientRect().right -
            buttons.firstElementChild.getBoundingClientRect().left
          : 0;
      const width = Math.ceil(
        Math.max(listRight - input.getBoundingClientRect().left, buttonsWidth)
      );
      setBlockWidth(width > 0 ? width : null);
    };

    measure();
    const ro = new ResizeObserver(measure);
    if (columnsRef.current) ro.observe(columnsRef.current);
    if (measureListRef.current) ro.observe(measureListRef.current);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, loading, isCompactLayout, columnSplitIndex, nameColumnWidths, isFiltered]);

  const isDateSortActive = sortBy === SORT_DATE_ASC || sortBy === SORT_DATE_DESC;

  /** Two-state toggle, as on the other birthday pages: clicking Date flips it. */
  const dateSortButton = (
    <button
      type="button"
      className={`dashboard-detail-sort-btn ${isDateSortActive ? "active" : ""}`}
      onClick={() => setSortBy(sortBy === SORT_DATE_ASC ? SORT_DATE_DESC : SORT_DATE_ASC)}
      title="Sort by Date"
    >
      Date
      {isDateSortActive ? (
        <SortDirectionIcon direction={sortBy === SORT_DATE_ASC ? "asc" : "desc"} />
      ) : null}
    </button>
  );

  const renderHeadRow = (line) => {
    const headMember = {
      ...(line.headRecord || {}),
      head_id: line.headId,
      color_name: line.colorName,
      hex_code: line.hexCode,
    };

    return (
      <div className="dashboard-detail-row family-heads-head-row">
        <span
          className={`family-heads-head-heading dashboard-section-heading${
            isLightLineColor(line.hexCode) ? " family-heads-head-heading--light" : ""
          }`}
        >
          <DashboardPhotoHeadLine
            name={line.displayHeadName}
            hexCode={line.hexCode}
            colorName={line.colorName ? `${line.colorName} Line` : ""}
            imageUrl={getDashboardMemberPhotoUrl(line.headRecord)}
            onViewPhoto={openPhoto}
            member={headMember}
            headsMap={headsMap}
          />
          <span className="family-heads-count dashboard-section-heading-count">
            ({line.displayCount})
          </span>
        </span>
      </div>
    );
  };

  // Same name format as Family Heads, then the birth month and day columns of
  // the printed report.
  const renderMemberRow = (member) => {
    const multipleBirthSup = getMultipleBirthSuperscriptText(member);
    // In-laws carry the asterisk the legend keys, as on In-Law Birthdays —
    // except the Start/Top of Line couple, who head the family rather than marry in.
    const showInLawAsterisk =
      isDashboardInLawMember(member) && !isExplicitDashboardStartOrTopOfLineMember(member);

    return (
      <div className="dashboard-detail-row family-heads-member-row birthdays-by-lines-member-row">
        <div className="dashboard-detail-name-col birthdays-by-lines-name-col">
          <DashboardNameMarkerGroup>
            <DashboardPhotoName {...getDashboardMemberNamePhotoUrls(member)} onViewPhoto={openPhoto}>
              <DashboardMemberNameLabel
                baseName={getMemberBaseName(member)}
                descendantSpouseName=""
              />
            </DashboardPhotoName>
            {showInLawAsterisk && <DashboardInLawAsterisk />}
            {isDashboardDeceasedMember(member) && <DashboardDeceasedAngel />}
            {multipleBirthSup && (
              <sup className="dashboard-multiple-birth-sup">{multipleBirthSup}</sup>
            )}
          </DashboardNameMarkerGroup>
        </div>
        <span className="birthdays-by-lines-date-col">
          {getMonthName(member)}
          {member.day != null && (
            <span className="birthdays-by-lines-day">{` ${member.day}`}</span>
          )}
        </span>
      </div>
    );
  };

  const renderItem = (item) =>
    item.type === "head" ? renderHeadRow(item.line) : renderMemberRow(item.member);

  const pageStyle = {
    ...(searchWidth ? { "--family-heads-search-width": `${searchWidth}px` } : {}),
    ...(blockWidth ? { "--birthdays-by-lines-block-width": `${blockWidth}px` } : {}),
  };

  return (
    <div
      ref={pageRef}
      className="dashboard-container family-heads-grand-count-page birthdays-by-family-lines-page dashboard-detail-page"
      style={pageStyle}
    >
      <div className="dash-box dashboard-detail-box">
        <div className="dashboard-detail-header">
          <div className="dashboard-detail-header-left">
            <DashboardBackButton />
            <h3 className="dashboard-detail-title">Birthdays by Family Lines</h3>
            <div className="dashboard-detail-count">{totalCount}</div>
            {!loading && (
              <DashboardDetailSearchBox
                value={searchQuery}
                onChange={setSearchQuery}
                // Picking a line or searching leaves the box and the sorts
                // where the full report centred them.
                freezeCentre={isFiltered}
              />
            )}
            {!loading && (
              <div ref={sortBarRef} className="family-heads-sort-bar">
                <DashboardDetailSortFilterBar
                  // The default view (birthday order inside lines that already
                  // run oldest to youngest by head) is handed over as "no sort
                  // picked", so the bar grays out Oldest to Youngest as the
                  // order already on screen, as Family Heads does.
                  sortBy={sortBy === DEFAULT_SORT ? "none" : sortBy}
                  defaultSortByMemberFilter={{ [DASHBOARD_MEMBER_FILTERS.ALL]: "oldest" }}
                  // Un-picking a sort falls back to the default order, not to none.
                  onSortChange={(next) => setSortBy(next === "none" ? DEFAULT_SORT : next)}
                  showMemberFilters={false}
                  leadingSortButtons={
                    <>
                      <div className="dashboard-detail-period" role="group" aria-label="Family line">
                        <DashboardPeriodDropdown
                          label="Line"
                          options={lineOptions}
                          value={lineFilter}
                          onChange={(next) => setLineFilter(String(next))}
                        />
                        <span className="dashboard-detail-sort-divider" aria-hidden="true" />
                      </div>
                      {dateSortButton}
                    </>
                  }
                />
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="dashboard-detail-empty-state">
            <p>Loading birthdays by family lines...</p>
          </div>
        ) : items.length === 0 ? (
          <div className="dashboard-detail-empty-state">
            {isSearching ? (
              <DashboardDetailSearchNoMatch query={searchQuery} noun="family members" />
            ) : (
              <p>No data available.</p>
            )}
          </div>
        ) : (
          <div className="family-heads-content">
            <div ref={measureListRef} className="family-heads-list family-heads-list-measure" aria-hidden="true">
              {items.map((item) => (
                <div key={`measure-${item.key}`} className={`family-heads-item family-heads-item--${item.type}`}>
                  {renderItem(item)}
                </div>
              ))}
            </div>
            <div ref={columnsRef} className="family-heads-columns">
              <div
                ref={firstColumnRef}
                className={`family-heads-list family-heads-list-first${
                  isSingleColumnList ? " family-heads-list--scrollable" : ""
                }`}
                style={
                  nameColumnWidths.first
                    ? { "--birthdays-by-lines-name-width": `${nameColumnWidths.first}px` }
                    : undefined
                }
              >
                {firstColumnItems.map((item) => (
                  <div key={item.key} className={`family-heads-item family-heads-item--${item.type}`}>
                    {renderItem(item)}
                  </div>
                ))}
              </div>
              {secondColumnItems.length > 0 && (
                <>
                  <div className="family-heads-column-divider" />
                  <div
                    className="family-heads-list family-heads-list-scroll"
                    style={
                      nameColumnWidths.second
                        ? { "--birthdays-by-lines-name-width": `${nameColumnWidths.second}px` }
                        : undefined
                    }
                  >
                    {secondColumnItems.map((item) => (
                      <div key={item.key} className={`family-heads-item family-heads-item--${item.type}`}>
                        {renderItem(item)}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        {!loading && (
          <div className="family-heads-footer in-law-footer">
            <DashboardMarkerLegend
              multipleBirthTags={getMultipleBirthLegendTags(
                items.filter((item) => item.type === "member").map((item) => item.member)
              )}
            />
            {/* Phone only, as on Family Members: the wide layout has the room
                to name every line in its heading and needs no key. */}
            {isCompactLayout && legendLines.length > 0 && (
              <DashboardColorLegend lines={legendLines} />
            )}
          </div>
        )}
      </div>
      <DashboardPhotoModal photos={photoModalPhotos} onClose={closePhoto} />
      <DashboardPhotoTapHint />
    </div>
  );
}

export default BirthdaysByFamilyLines;
